import { canonTag, resolveTag, tagIndex } from './tags.js';
import { projectStatus, isProjectLive } from './projectstatus.js';

// Project tags (Lj, Oct 11: "I renamed The Olivers to The Poly Palace and then tag it #olivers so I can run reports on
// it later, search, whatever"). A project's `tags` (edited in Project settings, normalised by commands.updateProject)
// are read through the suite list like every other tag (resolveTag → canonTag). Time on a tagged project = every time
// entry on every task of that project. Pure.
//
// The double-count rule: a task's time counts ONCE per tag. A task that carries a tag itself counts as task-tag time;
// its project's tag adds the same time only when the task doesn't carry that tag ("via project tag").

const num = (v) => (typeof v === 'number' ? v : Number(v) || 0);
const live = (x) => x && !x.deletedAt;
const pad = (n) => String(n).padStart(2, '0');
const dayStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (date, n) => { const d = new Date(`${date}T12:00:00`); d.setDate(d.getDate() + n); return dayStr(d); };
const todayStr = () => dayStr(new Date());

/** A tag list as suite slugs (merges followed), no duplicates. */
export function tagSlugSet(tags, opsTags) {
  const idx = tagIndex(opsTags);
  const out = new Set();
  for (const t of Array.isArray(tags) ? tags : []) { const k = t ? canonTag(resolveTag(t, idx)) : ''; if (k) out.add(k); }
  return out;
}

/** A project's tags as suite slugs, in the order typed: ['olivers', 'addition']. */
export const projectTagSlugs = (project, opsTags) => [...tagSlugSet(project?.tags, opsTags)];

/** projectId → [slug] for every live project with tags. */
export function projectTagMap(projects, opsTags) {
  const idx = tagIndex(opsTags);
  const m = new Map();
  for (const p of projects || []) if (live(p) && p.id) { const s = projectTagSlugs(p, idx); if (s.length) m.set(p.id, s); }
  return m;
}

/** The (live, not deleted) projects carrying a tag. */
export function projectsWithTag(tag, projects, opsTags) {
  const idx = tagIndex(opsTags);
  const slug = tag ? canonTag(resolveTag(tag, idx)) : '';
  if (!slug) return [];
  return (projects || []).filter((p) => live(p) && tagSlugSet(p.tags, idx).has(slug));
}

/**
 * The Projects page's tag chips: every tag on the given projects with how many carry it, most used first, then by
 * name. `keep(project)` narrows the pool (the status tab). → [{ slug, count }].
 */
export function projectTagChips(projects, { tags: opsTags = [], keep = null } = {}) {
  const idx = tagIndex(opsTags);
  const m = new Map();
  for (const p of projects || []) {
    if (!live(p) || (keep && !keep(p))) continue;
    for (const k of tagSlugSet(p.tags, idx)) m.set(k, (m.get(k) || 0) + 1);
  }
  return [...m.entries()].map(([slug, count]) => ({ slug, count })).sort((a, b) => b.count - a.count || a.slug.localeCompare(b.slug));
}

/** Multi-select tag filter: a project passes when it carries ANY picked tag (no picks = everything passes). */
export function projectHasAnyTag(project, picked, opsTags) {
  const want = (picked || []).map((t) => canonTag(t)).filter(Boolean);
  if (!want.length) return true;
  const have = tagSlugSet(project?.tags, opsTags);
  return want.some((t) => have.has(t));
}

/**
 * Search: does a project match the typed text? Name, address and customer as before, plus its tags — "#olivers"
 * matches tags only (a prefix of one), "olivers" matches the name or any tag containing it.
 */
export function projectMatches(project, q, opsTags) {
  const raw = String(q || '').trim().toLowerCase();
  if (!raw) return true;
  const tags = projectTagSlugs(project, opsTags);
  if (raw.startsWith('#')) { const k = canonTag(raw); return !!k && tags.some((t) => t.startsWith(k)); }
  if (`${project?.name || ''} ${project?.address || ''} ${project?.customerName || ''}`.toLowerCase().includes(raw)) return true;
  const k = canonTag(raw);
  return !!k && tags.some((t) => t.includes(k));
}

/**
 * Time on one tag, split the no-double-count way, over every live task:
 *   taskMs        on tasks carrying the tag themselves
 *   viaProjectMs  on tasks of projects carrying the tag, where the task doesn't carry it itself
 *   projectMs     on every task of the tagged projects (the Tag page's "12.5 h this month on 2 projects")
 *   totalMs       taskMs + viaProjectMs (each hour once)
 * for this month (`month`, YYYY-MM of today) and all time (`all`), plus `days`: the last `days` days of projectMs,
 * oldest first ([{ date, ms }], the sparkline), and `byProject`: projectId → { ms, monthMs, open }.
 */
export function tagTime(tag, { tasks = [], projects = [], tags: opsTags = [], today = todayStr(), days = 30 } = {}) {
  const idx = tagIndex(opsTags);
  const slug = tag ? canonTag(resolveTag(tag, idx)) : '';
  const tagged = new Set(projectsWithTag(slug, projects, idx).map((p) => p.id));
  const month = today.slice(0, 7);
  const from = addDays(today, -(days - 1));
  const z = () => ({ taskMs: 0, viaProjectMs: 0, projectMs: 0, totalMs: 0 });
  const out = { slug, month: z(), all: z(), days: [], byProject: new Map() };
  const perDay = new Map();
  for (const id of tagged) out.byProject.set(id, { ms: 0, monthMs: 0, open: 0 });
  if (!slug) return out;
  for (const t of tasks || []) {
    if (!live(t)) continue;
    const carries = tagSlugSet(t.tags, idx).has(slug);
    const inProj = !!t.projectId && tagged.has(t.projectId);
    if (!carries && !inProj) continue;
    const bp = inProj ? out.byProject.get(t.projectId) : null;
    if (bp && t.status === 'todo' && !t.bucket && !t.parentId) bp.open += 1;
    for (const e of t.timeEntries || []) {
      const ms = num(e?.duration);
      if (!(ms > 0) || !e.date) continue;
      const add = (o) => {
        if (carries) o.taskMs += ms; else o.viaProjectMs += ms;
        if (inProj) o.projectMs += ms;
        o.totalMs += ms;
      };
      add(out.all);
      if (String(e.date).slice(0, 7) === month) add(out.month);
      if (bp) { bp.ms += ms; if (String(e.date).slice(0, 7) === month) bp.monthMs += ms; }
      if (inProj && e.date >= from && e.date <= today) perDay.set(e.date, (perDay.get(e.date) || 0) + ms);
    }
  }
  for (let i = 0; i < days; i++) { const d = addDays(from, i); out.days.push({ date: d, ms: perDay.get(d) || 0 }); }
  return out;
}

/** The Tag page's project rows: [{ project, status, live, open, ms, monthMs }] — live first, then most time. */
export function tagProjectRows(tag, src = {}, time = null) {
  const t = time || tagTime(tag, src);
  const byId = new Map((src.projects || []).map((p) => [p.id, p]));
  return [...t.byProject.entries()].map(([id, x]) => {
    const p = byId.get(id);
    return { project: p, id, name: p?.name || 'Project', status: projectStatus(p), live: isProjectLive(p), ...x };
  }).sort((a, b) => (b.live - a.live) || b.ms - a.ms || a.name.localeCompare(b.name));
}

/** "12.5 h" — hours to one decimal, whole hours without one. */
export const hoursText = (ms) => { const h = Math.round((num(ms) / 3600000) * 10) / 10; return `${h} h`; };

/** "12.5 h this month on 2 projects" (or "No time this month on 1 project"). */
export function projectTimeLine(monthMs, n) {
  const on = `on ${n} project${n === 1 ? '' : 's'}`;
  return monthMs > 0 ? `${hoursText(monthMs)} this month ${on}` : `No time this month ${on}`;
}
